import React, { useState } from "react";
import { Loader2, Check, Home as HomeIcon, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, isValidEmail } from "../../lib/api";
import { useSeo } from "../../lib/useSeo";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TERMS = [
  { value: "0-3", label: "0–3 maanden" },
  { value: "3-6", label: "3–6 maanden" },
  { value: "6-12", label: "6–12 maanden" },
  { value: "orienterend", label: "Oriënterend" },
];

const Waardebepaling = () => {
  useSeo({
    title: "Gratis waardebepaling — wat is jouw woning waard? | WoningAlert",
    description: "Overweeg je te verkopen? Vraag vrijblijvend een gratis waardebepaling aan en kom in contact met een makelaar bij jou in de buurt.",
    image: "/og-image.png",
  });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    postcode: "", house_number: "", house_number_addition: "",
    property_type: "", m2_estimate: "", timeline: "", has_agent: "",
    name: "", email: "", phone: "", consent: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const next = () => {
    if (!f.postcode.trim() || !f.house_number.trim()) return toast.error("Vul postcode en huisnummer in.");
    setStep(2);
  };

  const submit = async () => {
    if (!f.name.trim() || !isValidEmail(f.email) || !f.phone.trim()) return toast.error("Vul naam, geldig e-mail en telefoon in.");
    if (!f.consent) return toast.error("Ga akkoord met de privacyverklaring.");
    setBusy(true);
    try {
      await api("/leads", {
        method: "POST",
        body: JSON.stringify({
          lead_type: "valuation", name: f.name, email: f.email, phone: f.phone,
          property_id: null, consent: true, source: "web",
          qualification: {
            postcode: f.postcode, house_number: f.house_number, house_number_addition: f.house_number_addition,
            property_type: f.property_type, m2_estimate: f.m2_estimate ? Number(f.m2_estimate) : null,
            timeline: f.timeline, has_agent: f.has_agent,
          },
          attribution: { source_page: "/waardebepaling", referring_flow: "waardebepaling_page", platform: "web" },
        }),
      });
      setStep(3);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="relative overflow-hidden bg-navy">
        <div className="pointer-events-none absolute -right-16 -top-20 h-80 w-80 rounded-full bg-brandblue/30 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white"><HomeIcon size={15} /> Voor verkopers</span>
          <h1 className="mt-4 font-display text-4xl font-extrabold text-white sm:text-5xl">Wat is jouw woning waard?</h1>
          <p className="mx-auto mt-3 max-w-xl text-lg text-white/70">Overweeg je te verkopen? Vraag vrijblijvend een gratis waardebepaling aan en kom in contact met een makelaar.</p>
        </div>
      </div>

      <div className="mx-auto -mt-10 max-w-lg px-4 pb-16 sm:px-6">
        <div className="rounded-3xl border border-border bg-white p-7 shadow-xl" data-testid="waardebepaling-form">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-navy">Om welke woning gaat het?</h2>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-3 sm:col-span-1"><Label>Postcode</Label><Input data-testid="wb-postcode" value={f.postcode} onChange={(e) => set("postcode", e.target.value)} placeholder="1234 AB" className="mt-1.5 rounded-xl" /></div>
                <div><Label>Huisnr.</Label><Input data-testid="wb-housenumber" value={f.house_number} onChange={(e) => set("house_number", e.target.value)} className="mt-1.5 rounded-xl" /></div>
                <div><Label>Toevoeging</Label><Input value={f.house_number_addition} onChange={(e) => set("house_number_addition", e.target.value)} className="mt-1.5 rounded-xl" /></div>
              </div>
              <Button data-testid="wb-next" onClick={next} className="w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90">
                Gratis waardebepaling aanvragen <ArrowRight size={18} className="ml-2" />
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck size={13} /> Vrijblijvend · geen kosten · geen verplichtingen</p>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-navy">Nog een paar details</h2>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Woningtype</Label>
                  <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid="wb-type"><SelectValue placeholder="Kies…" /></SelectTrigger>
                    <SelectContent>{["huis", "appartement", "studio", "penthouse", "villa", "overig"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Woonopp. (m², optioneel)</Label><Input type="number" value={f.m2_estimate} onChange={(e) => set("m2_estimate", e.target.value)} className="mt-1.5 rounded-xl" /></div>
              </div>
              <div><Label>Gewenste verkooptermijn</Label>
                <Select value={f.timeline} onValueChange={(v) => set("timeline", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="wb-timeline"><SelectValue placeholder="Kies…" /></SelectTrigger>
                  <SelectContent>{TERMS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Al een verkoopmakelaar?</Label>
                <Select value={f.has_agent} onValueChange={(v) => set("has_agent", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="Kies…" /></SelectTrigger>
                  <SelectContent><SelectItem value="ja">Ja</SelectItem><SelectItem value="nee">Nee</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-3 border-t border-border pt-4">
                <div><Label>Naam</Label><Input data-testid="wb-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>E-mail</Label><Input data-testid="wb-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-xl" /></div>
                  <div><Label>Telefoon</Label><Input data-testid="wb-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 rounded-xl" /></div>
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-500">
                <input data-testid="wb-consent" type="checkbox" className="mt-0.5" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} />
                <span>Ik geef toestemming om contact met mij op te nemen over deze waardebepaling (<a href="/privacy" target="_blank" rel="noreferrer" className="text-brandblue underline">privacy</a>).</span>
              </label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl">Terug</Button>
                <Button data-testid="wb-submit" onClick={submit} disabled={busy} className="flex-1 rounded-xl bg-brandblue hover:bg-brandblue/90">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : "Verstuur aanvraag"}
                </Button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="py-6 text-center" data-testid="wb-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success"><Check size={28} /></div>
              <h2 className="mt-4 font-display text-xl font-bold text-navy">Aanvraag ontvangen!</h2>
              <p className="mt-1 text-sm text-slate-500">Een makelaar neemt zo snel mogelijk contact met je op voor je gratis waardebepaling.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Waardebepaling;
